var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { cacheService } from "../../cache";
import { ModelScriptBase } from "../model/ModelScriptBase";
import { FUNCTION_SCRIPT_IMPORTS, MODEL_SCRIPT_IMPORTS, PARAMETER_SCRIPT_IMPORTS } from "../model/ModelScriptImportsRegistry";
import { ScriptBase } from "./ScriptBase";
const ALLOWED_GLOBALS = new Set(["Math", "console", "JSON", "Error", "TypeError", "Number", "Boolean", "String", "parseFloat"]);
const ORIGIN = location.origin;
// https://blog.risingstack.com/writing-a-javascript-framework-sandboxed-code-evaluation/
function has(target, key) {
    return ALLOWED_GLOBALS.has(key) ? key in target : true;
}
function get(target, key) {
    if (key === Symbol.unscopables)
        return undefined;
    return target[key];
}
const SCRIPT_FUNCTION_NAMES = [
    "onDestroy",
    "onInit",
    "onStart",
    "onVisible",
    "onUpdate",
    "onTransformUpdate",
    "onClick",
    "onSelect",
    "onDeselect",
    "onEnterContact",
    "onPrimaryButton",
    "onSecondaryButton",
    "onTertiaryButton",
    "onQuaternaryButton",
    "onLeaveContact",
    "onButton",
    "onInputAxis",
    "onGrabPickup",
    "onGrabReleased",
    "onHostChanged",
    "onEnterRoom",
    "onLeaveRoom",
    "onPlayerSpawned",
    "onPlayerEntersRoom",
    "onPlayerLeavesRoom",
    "onWebXrPresentingChanged"
];
export const SCRIPT_FUNCTION_NAMES_SET = new Set();
for (const name of SCRIPT_FUNCTION_NAMES) {
    SCRIPT_FUNCTION_NAMES_SET.add(name);
}
export class ScriptManager {
    constructor() {
        this.scripts = new Map();
        this.compiledModelScripts = new Map();
        this.compiledParametersScripts = new Map();
        this.compiledFunctionScripts = new Map();
        this.cachedFunctions = new Map();
    }
    addScript(scriptId, script) {
        if (this.scripts.has(scriptId)) {
            throw new Error(`Script with id ${scriptId} already added`);
        }
        this.scripts.set(scriptId, Promise.resolve(script));
    }
    scriptChanged(scriptId) {
        this.compiledModelScripts.delete(scriptId);
        this.compiledFunctionScripts.delete(scriptId);
    }
    loadScript(scriptId, generatedScript) {
        return __awaiter(this, void 0, void 0, function* () {
            let scriptUrl;
            if (scriptId.startsWith("/")) {
                scriptUrl = ORIGIN + scriptId;
            }
            else {
                const scriptExt = generatedScript ? "generated.*" : "script.*";
                scriptUrl = `${ORIGIN}/api/v1/asset/${scriptId}/${scriptExt}`;
            }
            return yield cacheService.loadText(scriptUrl, {
                method: "GET"
            });
        });
    }
    loadScriptPromise(scriptId, generatedScript) {
        return __awaiter(this, void 0, void 0, function* () {
            const loadedPromise = new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                try {
                    const scriptStr = yield this.loadScript(scriptId, generatedScript);
                    resolve(scriptStr);
                }
                catch (error) {
                    console.error("Script loading error: ", error);
                    reject(error);
                }
            }));
            this.scripts.set(scriptId, loadedPromise);
            return yield loadedPromise;
        });
    }
    getModelScript(scriptId, generatedScript, modelUID, onScriptLoad, onScriptLoaded) {
        return __awaiter(this, void 0, void 0, function* () {
            const compiledScript = this.compiledModelScripts.get(scriptId);
            if (compiledScript != null) {
                return yield compiledScript;
            }
            const promise = new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                try {
                    if (onScriptLoad != null) {
                        onScriptLoad();
                    }
                    const scriptStr = yield this.loadScript(scriptId, generatedScript);
                    resolve(this.compileModelScript(scriptStr));
                    if (onScriptLoaded != null) {
                        onScriptLoaded();
                    }
                }
                catch (error) {
                    console.error("ScriptError: ", error, scriptId, modelUID);
                    reject(error);
                    if (onScriptLoaded != null) {
                        onScriptLoaded(error);
                    }
                }
            }));
            this.compiledModelScripts.set(scriptId, promise);
            return yield promise;
        });
    }
    getParametersScripts(scriptId, generatedScript) {
        return __awaiter(this, void 0, void 0, function* () {
            const compiledScript = this.compiledParametersScripts.get(scriptId);
            if (compiledScript != null) {
                return yield compiledScript;
            }
            const promise = new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                try {
                    const scriptStr = yield this.loadScript(scriptId, generatedScript);
                    resolve(this.compileParametersScript(scriptStr));
                }
                catch (error) {
                    console.error("ScriptError: ", error);
                    reject(error);
                }
            }));
            this.compiledParametersScripts.set(scriptId, promise);
            return yield promise;
        });
    }
    getFunctionScript(scriptId, generatedScript) {
        return __awaiter(this, void 0, void 0, function* () {
            const compiledScript = this.compiledFunctionScripts.get(scriptId);
            if (compiledScript != null) {
                return yield compiledScript;
            }
            const loadedPromise = this.loadScriptPromise(scriptId, generatedScript);
            const compiledPromise = new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                try {
                    const scriptStr = yield loadedPromise;
                    const codeData = this.compileCode(scriptStr, FUNCTION_SCRIPT_IMPORTS);
                    resolve(this.compileFunctionScript(codeData));
                }
                catch (error) {
                    console.error("ScriptError: ", error);
                    reject(error);
                }
            }));
            this.compiledFunctionScripts.set(scriptId, compiledPromise);
            return yield compiledPromise;
        });
    }
    /**
     * create a new instance of the script
     */
    createFunctionScript(scriptId, generatedScript) {
        return __awaiter(this, void 0, void 0, function* () {
            let promise = this.cachedFunctions.get(scriptId);
            if (promise == null) {
                const loadedPromise = this.loadScriptPromise(scriptId, generatedScript);
                promise = new Promise((resolve) => __awaiter(this, void 0, void 0, function* () {
                    const scriptStr = yield loadedPromise;
                    const codeData = this.compileCode(scriptStr, FUNCTION_SCRIPT_IMPORTS);
                    resolve(codeData);
                }));
                this.cachedFunctions.set(scriptId, promise);
            }
            const codeData = yield promise;
            return this.compileFunctionScript(codeData);
        });
    }
    compileCode(scriptStr, imports) {
        scriptStr = scriptStr.replace(/export\s+class/, "return class");
        scriptStr = scriptStr.replace(/([ \t]*class[ \t]+\w+[ \t]+extends[ \t]+ModelScriptBase)/, "return $1");
        const scriptBody = `with (sandbox) {
      ${scriptStr}
    }`;
        const code = new Function("sandbox", scriptBody);
        const sandboxProxy = new Proxy(Object.assign(Object.assign({}, imports), { console: this.createSandboxConsole() }), { has, get });
        return {
            code,
            sandboxProxy
        };
    }
    createSandboxConsole() {
        const prefix = "[userScript]";
        const make = (method) => (...args) => {
            console[method](prefix, ...args);
        };
        return {
            log: make("log"),
            debug: make("debug"),
            info: make("info"),
            warn: make("warn"),
            error: make("error"),
            trace: make("trace"),
            group: make("group"),
            groupCollapsed: make("groupCollapsed"),
            groupEnd: () => {
                console.groupEnd();
            },
            table: make("table"),
            dir: make("dir"),
            dirxml: make("dirxml"),
            assert: (...args) => {
                console.assert(...args);
            },
            count: (...args) => {
                console.count(...args);
            },
            countReset: (...args) => {
                console.countReset(...args);
            },
            time: (...args) => {
                console.time(...args);
            },
            timeLog: (...args) => {
                console.timeLog(...args);
            },
            timeEnd: (...args) => {
                console.timeEnd(...args);
            },
            clear: () => {
                console.clear();
            },
            profile: (...args) => {
                console.profile(...args);
            },
            profileEnd: (...args) => {
                console.profileEnd(...args);
            },
            timeStamp: (...args) => {
                console.timeStamp(...args);
            }
        };
    }
    compileModelScript(scriptStr) {
        const codeData = this.compileCode(scriptStr, MODEL_SCRIPT_IMPORTS);
        const modelClass = codeData.code(codeData.sandboxProxy);
        if (modelClass === undefined)
            throw new Error("Script must return a model class");
        if (!(modelClass.prototype instanceof ModelScriptBase))
            throw new Error("Script class must extends ModelBase");
        for (const funcName of SCRIPT_FUNCTION_NAMES) {
            this.checkFunction(modelClass.prototype, funcName);
        }
        return modelClass;
    }
    compileParametersScript(scriptStr) {
        const codeData = this.compileCode(scriptStr, PARAMETER_SCRIPT_IMPORTS);
        const modelClass = codeData.code(codeData.sandboxProxy);
        if (modelClass === undefined)
            throw new Error("Script must return a class");
        if (!(modelClass.prototype instanceof ScriptBase))
            throw new Error("Script class must extends ScriptBase");
        this.checkFunction(modelClass.prototype, "onInit");
        this.checkFunction(modelClass.prototype, "onParamsChanged");
        return modelClass;
    }
    compileFunctionScript(compiledCodeData) {
        const functionScript = compiledCodeData.code(compiledCodeData.sandboxProxy);
        if (functionScript === undefined && typeof functionScript !== "function")
            throw new Error("Script must return a function");
        return functionScript;
    }
    checkFunction(prototype, funcName) {
        if (!(funcName in prototype))
            return false;
        if (typeof prototype[funcName] !== "function") {
            throw new Error(`${funcName} should be a Function`);
        }
        return true;
    }
    onLayouterChanged() {
        this.compiledModelScripts.clear();
        this.compiledFunctionScripts.clear();
        this.compiledParametersScripts.clear();
        this.cachedFunctions.clear();
    }
}
